import { create } from "zustand";
import { z } from "zod";
import { getPlatform } from "@/lib/platform";
import { SESSION_KEY } from "@/lib/storageKeys";
import { SUPPORTED_LOCALES, type SupportedLocale } from "@/i18n";

export const SessionUserSchema = z.object({
  // DUE-158 (iOS platform layer): null for iOS guest/Apple-auth users, who
  // have no Telegram identity. Always a number for Telegram users.
  tg_id: z.number().int().nullable(),
  locale: z.enum(SUPPORTED_LOCALES as unknown as [SupportedLocale, ...SupportedLocale[]]),
  credits: z.number().int().nonnegative(),
  // DUE-149: the backend serializes this via `datetime.isoformat()` on a
  // tz-aware column, which emits an explicit `+00:00` offset — never a `Z`.
  // zod's `.datetime()` rejects offsets unless `{ offset: true }` is set, so
  // without this a paying user (non-null value) failed the parse, got no
  // session, and looped on /onboarding/welcome.
  subscription_until: z.string().datetime({ offset: true }).nullable(),
  birth_profile_id: z.string().nullable(),
  is_admin: z.boolean(),
  // MAV-114: server-side flag for "wizard finished". The mini app uses
  // it as the gate to send the user to /onboarding instead of /home.
  // Decoupled from `birth_profile_id` so future reset/restart flows
  // can null one without nulling the other. Kept ISO string here to
  // mirror the backend payload — the only thing the FE checks is null.
  onboarding_completed_at: z.string().nullable(),
});

export type SessionUser = z.infer<typeof SessionUserSchema>;

export const SessionEnvelopeSchema = z.object({
  jwt: z.string().min(1),
  user: SessionUserSchema,
  // DUE-158 (iOS platform layer): device/apple/refresh auth on iOS returns a
  // refresh token alongside the JWT. `.optional()` because legacy cached
  // payloads (written before this field existed) have no such key at all.
  // Never persisted with the rest of the envelope — see `persist()` below,
  // which strips it; Task 5 owns writing it to secureStorage (Keychain).
  refresh_token: z.string().nullable().optional(),
});

export type SessionEnvelope = z.infer<typeof SessionEnvelopeSchema>;

export const SESSION_STORAGE_KEY = SESSION_KEY;

/** Decode the JWT payload and return `true` if it expires within `marginSec`.
 *
 * Payload-only peek — we don't verify the signature (only the server can do
 * that). A malformed or missing `exp` is treated as expired so callers
 * conservatively trigger reauth. */
export function isJwtExpiredOrNear(jwt: string, marginSec: number = 60): boolean {
  try {
    const parts = jwt.split(".");
    if (parts.length !== 3) return true;
    const payloadRaw = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payloadRaw + "=".repeat((4 - (payloadRaw.length % 4)) % 4);
    const json =
      typeof atob === "function"
        ? atob(padded)
        : Buffer.from(padded, "base64").toString("utf-8");
    const payload = JSON.parse(json) as { exp?: unknown };
    const exp = typeof payload.exp === "number" ? payload.exp : 0;
    const nowSec = Math.floor(Date.now() / 1000);
    return exp <= nowSec + marginSec;
  } catch {
    return true;
  }
}

interface SessionState {
  jwt: string | null;
  user: SessionUser | null;
  hydrated: boolean;
  setSession: (envelope: SessionEnvelope) => void;
  clear: () => void;
  hydrateFromStorage: () => Promise<void>;
}

function persist(envelope: SessionEnvelope | null): void {
  // Resolved per call (not hoisted to module load) so tests can swap the
  // platform via `__setPlatformForTests` before `setSession`/`clear` run,
  // and so module-load order never forces platform detection early.
  //
  // DUE-196: `Platform.storage` is device-local on every platform now. It
  // used to resolve to Telegram CloudStorage inside Telegram, which sent
  // the access JWT and the user profile to Telegram's servers on every
  // session write.
  const storage = getPlatform().storage;
  if (envelope === null) {
    void storage.removeItem(SESSION_STORAGE_KEY);
    return;
  }
  // The refresh token is credential material, not general app state — it
  // belongs in secureStorage (Task 5), never in this general session blob.
  const stored = { ...envelope };
  delete stored.refresh_token;
  void storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stored));
}

export const useSessionStore = create<SessionState>((set) => ({
  jwt: null,
  user: null,
  hydrated: false,
  setSession: (envelope) => {
    set({ jwt: envelope.jwt, user: envelope.user, hydrated: true });
    persist(envelope);
  },
  clear: () => {
    set({ jwt: null, user: null, hydrated: true });
    persist(null);
  },
  hydrateFromStorage: async () => {
    try {
      const raw = await getPlatform().storage.getItem(SESSION_STORAGE_KEY);
      if (!raw) {
        set({ hydrated: true });
        return;
      }
      const parsed = SessionEnvelopeSchema.safeParse(JSON.parse(raw));
      // A stored JWT that's about to expire (or already has) would 401 on
      // its first real use. Drop it at hydration time so the client's auth
      // flow reauths with initData before the first product request, and
      // parallel requests don't each get their own 401 while a refresh
      // races.
      if (parsed.success && !isJwtExpiredOrNear(parsed.data.jwt)) {
        set({ jwt: parsed.data.jwt, user: parsed.data.user, hydrated: true });
      } else {
        set({ hydrated: true });
      }
    } catch {
      set({ hydrated: true });
    }
  },
}));
